/**
 * The active company in the top bar. A person in one company sees its name, as before; a person
 * in several picks among them here. Switching moves the session (`POST /api/me/contractor`) and
 * reloads the app: every query in the cache — runs, flows, memories — belongs to the company left.
 */
import type { PortalContractor } from '@portal/contracts'
import { useState } from 'react'
import { switchContractor } from '../api/auth'
import { useI18n } from '../i18n'

export function CompanySwitcher({
  contractor,
  contractors
}: {
  contractor: PortalContractor | null
  contractors: PortalContractor[]
}) {
  const { t } = useI18n()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  if (!contractor) return null
  if (contractors.length < 2) return <span className="portal-tenant">{contractor.name}</span>

  const onChange = async (id: string) => {
    if (id === contractor.id) return
    setBusy(true)
    setFailed(false)
    try {
      await switchContractor(id)
      location.assign('/')
    } catch {
      setFailed(true)
      setBusy(false)
    }
  }

  return (
    <span className="portal-tenant-switch">
      <select
        className="select portal-tenant-select"
        aria-label={t.shell.switchCompany}
        value={contractor.id}
        disabled={busy}
        onChange={(e) => void onChange(e.target.value)}
      >
        {contractors.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      {failed && (
        <span className="portal-tenant-error" role="alert">
          {t.shell.switchCompanyFailed}
        </span>
      )}
    </span>
  )
}
